import axios from 'axios';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
import { getDeviceId, getEncryptionKey } from '@/utils/csrf';
import { useAuthStore } from '@/store/modules/auth';
import useTransactionsStore from '@/store/modules/transactions';
import { retry } from '@/common/services/retry';
import router from '@/router';
import { storeToRefs } from 'pinia';
import { TransactTypes } from '@/types/transact';
var FiatSettlementErrorReason = WidgetApiServiceTypes.FiatSettlementErrorReason;
const url = process.env.VUE_APP_WIDGET_API;
async function http(token, sessionId, deviceId) {
    let encryptionKey = '';
    try {
        encryptionKey = await getEncryptionKey();
    }
    catch (e) {
        console.error(`failed to get encryption key: ${e.message}`);
    }
    const params = {
        baseURL: url,
        headers: {
            'Content-Type': 'application/json',
            Authorization: token,
            DeviceId: deviceId,
            SessionId: sessionId,
            'x-csrf-token': encryptionKey,
        },
    };
    if (!token) {
        delete params.headers.Authorization;
    }
    if (!params.headers.DeviceId) {
        params.headers.DeviceId = await getDeviceId();
    }
    const instance = axios.create(params);
    instance.interceptors.response.use(response => response, async (error) => {
        // TODO: Make a new ticket for error handling.
        // Error handling needs to be done well on the backend, using validated error codes.
        // These codes can then be mapped in an error service in the vue app so we can
        // gracefully show errors to the user.
        if (error.response) {
            if (error.response.data.message.includes('User does not exist')) {
                throw new Error('User does not exist.');
            }
            else if (error.response.data.message.includes('failed to signup user')) {
                throw new Error('Failed to sign up user.');
            }
            else if ((error.response.data.message.includes('invalid apiKey') ||
                error.response.data.message.includes('invalid session')) &&
                error.response.status === 401) {
                // session expired, try to get new session
                const auth = useAuthStore();
                await auth.VerifyApiKey({ apiKey: auth.partnerKey });
                // reset the session if in the original request
                error.config.headers.SessionId = auth.partnerSessionId;
                // retry original request
                return axios(error.config);
            }
            else if (error.response.data.message.includes('incorrect token supplied')) {
                // refresh token has expired, need to log user out
                const authStore = useAuthStore();
                const transactionsStore = useTransactionsStore();
                transactionsStore.resetStore();
                authStore.resetStore();
                const { currentStep: transactStep } = storeToRefs(transactionsStore);
                transactStep.value = TransactTypes.TransactSteps.BUY_1;
                router.push('/transact');
                console.error('refresh token invalid/expired');
                return axios(error.config);
            }
            else if (error.response.data.code === 'DailyLimitExceededError') {
                const transaction = useTransactionsStore();
                transaction.limitError = true;
                transaction.dailyLimitError = true;
                return axios(error.config);
            }
            else if (error.response.data.code === 'MonthlyLimitExceededError') {
                const transaction = useTransactionsStore();
                transaction.limitError = true;
                transaction.monthlyLimitError = true;
                return axios(error.config);
            }
            else if (error.response.data.code === 'LifetimeLimitExceededError') {
                const transaction = useTransactionsStore();
                transaction.limitError = true;
                transaction.lifetimeLimitError = true;
                return axios(error.config);
            }
            else {
                if (error.response.status === 401) {
                    const authStore = useAuthStore();
                    try {
                        await authStore.CheckAccessToken();
                        error.config.headers.Authorization = authStore.token;
                        return axios(error.config);
                    }
                    catch (e) {
                        const transactionsStore = useTransactionsStore();
                        transactionsStore.resetStore();
                        authStore.resetStore();
                        const { currentStep: transactStep } = storeToRefs(transactionsStore);
                        transactStep.value = TransactTypes.TransactSteps.BUY_1;
                        router.push('/transact');
                        console.error('token invalid/expired');
                        return axios(error.config);
                    }
                }
                throw new Error(error.response.data.message);
            }
        }
        else if (error.code === 'ECONNABORTED') {
            throw new Error('Network error. Please try again.');
        }
        else if (error.code === 'ERR_NETWORK') {
            const authStore = useAuthStore();
            try {
                await authStore.CheckAccessToken();
                error.config.headers.Authorization = authStore.token;
                return axios(error.config);
            }
            catch (e) {
                const transactionsStore = useTransactionsStore();
                transactionsStore.resetStore();
                authStore.resetStore();
                const { currentStep: transactStep } = storeToRefs(transactionsStore);
                transactStep.value = TransactTypes.TransactSteps.BUY_1;
                router.push('/transact');
                console.error('refresh token invalid/expired');
                return axios(error.config);
            }
        }
        else {
            throw new Error('Unexpected error occurred.');
        }
    });
    return instance;
}
const WidgetApiService = {
    async SignIn(payload, sessionId, deviceId) {
        const api = await http(undefined, sessionId, deviceId);
        const response = await api.post('user/signin', payload);
        return response.data;
    },
    async SignUp(payload, sessionId, deviceId) {
        const api = await http(undefined, sessionId, deviceId);
        const response = await api.post('user/signup', payload);
        return response.data;
    },
    async SubmitCode(payload, sessionId, deviceId) {
        const api = await http(undefined, sessionId, deviceId);
        const response = await api.post('user/submit-code', payload);
        return response.data;
    },
    async RefreshAccessToken(payload, sessionId, deviceId) {
        const api = await http(undefined, sessionId, deviceId);
        const response = await api.post('user/refresh', payload);
        return response.data;
    },
    async GetRates(sessionId, deviceId) {
        const api = await http(undefined, sessionId, deviceId);
        const response = await api.get('rates');
        return response.data;
    },
    async GetUserTransactions(token, sessionId, deviceId) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.get(`transactions`);
        if (!response.data)
            return { payments: [], collections: [] };
        return response.data;
    },
    async GetTierLimit(token, tier, countryCode, sessionId, deviceId) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.get(`tier/limit?tier=${tier}&countryCode=${countryCode}`);
        return response.data;
    },
    async Logout(token, sessionId, deviceId) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.post('user/logout');
        return response.data;
    },
    async ResolveAccountName(token, sessionId, deviceId, networkId, accountNumber, signal) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.get(`resolve/bank?networkId=${networkId}&accountNumber=${accountNumber}`, { signal });
        return response.data;
    },
    async GetNetworks(token, sessionId, deviceId, channelId, country, accountType) {
        const api = await http(token, sessionId, deviceId);
        const props = {};
        if (country)
            props.country = country;
        if (accountType)
            props.accountType = accountType;
        if (channelId)
            props.channelId = channelId;
        const params = new URLSearchParams(props);
        const response = await api.get(`networks?${params.toString()}`);
        return response.data.filter((network) => !(network.tempDisabledFor || []).includes(props.channelId));
    },
    async GetChannels(prop) {
        const { sessionId, deviceId, rate, amount, currency, cryptoAmount, cryptoCurrency, rampType, cryptoNetwork, withoutFees, } = prop;
        const api = await http(undefined, sessionId, deviceId);
        const params = new URLSearchParams({
            rate,
            amount,
            currency,
            cryptoAmount,
            cryptoCurrency,
            rampType,
            cryptoNetwork,
            withoutFees,
        });
        const response = await api.get(`channels?${params.toString()}`);
        return response.data;
    },
    async GetCountryStates(token, sessionId, deviceId, countryCode) {
        return retry(async () => {
            const api = await http(token, sessionId, deviceId);
            const response = await api.get(`states?countryCode=${countryCode}`);
            return response.data;
        });
    },
    async CreateOrder(token, order, sessionId, deviceId) {
        try {
            const api = await http(token, sessionId, deviceId);
            const response = await api.post('user/order', order);
            return response.data;
        }
        catch (e) {
            console.error(e);
            throw new Error(e.response.data.message);
        }
    },
    async CreateSellOrder(token, order, sessionId, deviceId) {
        try {
            const api = await http(token, sessionId, deviceId);
            const response = await api.post('user/withdraw', order);
            return response.data;
        }
        catch (e) {
            console.error(e);
            throw new Error(e.response.data.message);
        }
    },
    async FetchPayment(token, sessionId, deviceId, paymentId) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.get(`payment?paymentId=${paymentId}`);
        return response.data;
    },
    async FetchCollection(token, sessionId, deviceId, collectionId) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.get(`collection?collectionId=${collectionId}`);
        return response.data;
    },
    async CancelPayment(token, sessionId, deviceId, payload) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.post(`user/withdraw/fail`, payload);
        return response.data;
    },
    async VerifyApiKey(payload) {
        const api = await http();
        const response = await api.post('verify', payload);
        return response.data;
    },
    async BeginKYC(token, sessionId, deviceId, payload) {
        const api = await http(token, sessionId, deviceId);
        await api.post('kyc/begin', payload);
    },
    async GetKycUploadUrls(token, sessionId, deviceId, payload) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.post('kyc/upload-urls', payload);
        return response?.data;
    },
    async GetKycStatus(token, sessionId, deviceId) {
        const api = await http(token, sessionId, deviceId);
        const response = await api.get('kyc/status');
        return response?.data;
    },
    async GetPayoutAccounts(token, sessionId, deviceId) {
        const api = await http(token, sessionId, deviceId);
        const { data } = await api.get('user/payout-account');
        return data;
    },
    async AddPayoutAccount(token, sessionId, deviceId, payload) {
        const api = await http(token, sessionId, deviceId);
        const { data } = await api.post('user/payout-account', payload);
        return data;
    },
    async UpdatePayoutAccount(token, sessionId, deviceId, payload) {
        const api = await http(token, sessionId, deviceId);
        const { data } = await api.put('user/payout-account', payload);
        return data;
    },
    async DeletePayoutAccount(token, sessionId, deviceId, payload) {
        const api = await http(token, sessionId, deviceId);
        const { data } = await api.delete(`user/payout-account/${payload.id}`);
        return data;
    },
    async GetTravelRuleConfig(token, sessionId, deviceId, countryCode) {
        const api = await http(token, sessionId, deviceId);
        const { data } = await api.get(`user/travel-rule?countryCode=${countryCode}`);
        return data;
    },
    async CheckForExistingId(token, sessionId, deviceId, payload) {
        const api = await http(token, sessionId, deviceId);
        const { data } = await api.post('kyc/check-id-exists', payload);
        return data;
    },
    async GetFiatSettlement(sequenceId, sessionId, deviceId) {
        const api = await http(undefined, sessionId, deviceId);
        const params = new URLSearchParams({ sequenceId });
        const { data } = await api.get(`fiat-settlement?${params.toString()}`);
        return data;
    },
    async AcceptFiatSettlement(sequenceId, sessionId, deviceId) {
        const api = await http(undefined, sessionId, deviceId);
        const { data } = await api.post('fiat-settlement', {
            sequenceId,
            action: 'accept',
        });
        return data;
    },
};
// Maps the widget-api fiat settlement error messages onto the states the
// authorize view renders. The interceptor above drops the response code, so
// matching on the message is the only signal left.
export function fiatSettlementErrorReason(message) {
    const text = message?.toLowerCase() ?? '';
    if (text.includes('not found'))
        return FiatSettlementErrorReason.NOT_FOUND;
    if (text.includes('has expired'))
        return FiatSettlementErrorReason.EXPIRED;
    if (text.includes('can no longer be authorized')) {
        return FiatSettlementErrorReason.INVALID_STATE;
    }
    return FiatSettlementErrorReason.UNKNOWN;
}
export default WidgetApiService;
